import { Button, Dropdown, MenuProps, Modal, Space, Spin, Table, Typography } from 'antd';
import { ColumnType } from 'antd/lib/table';
import dayjs from 'dayjs';
import _ from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import { match as routerMatch } from 'react-router-dom';
import { Dispatch } from 'redux';
import PermissionsEnum from '~Api/Administrator/PermissionsEnum';
import IApplication from '~Api/Application/IApplication';
import IFormal from '~Api/Application/IFormal';
import {
    applicationFormalApprovalSendToBorrowerAction,
    applicationFormalApprovalSendToBrokerAction,
    applicationFormalsAddAction,
    applicationFormalsListAction,
    applicationGetAction,
} from '~Applications/actions';
import {
    applicationFormalsSelector,
    applicationSelector,
    applicationWorkflowStatusMoveInProgressSelector,
} from '~Applications/selectors';
import IAuthUser from '~Auth/IAuthUser';
import { authCurrentUserSelector } from '~Auth/selectors';
import { IGlobalState } from '~reducer';
import Layout from './Layout';
import FormalApprovalEditModal from './FormalApprovalEditModal';
import FormalApprovalSendModal from './FormalApprovalSendModal';
import ModalStateEnum from '~UI/ModalStateEnum';
import { IDictionary } from '~utilities/IDictionary';

interface IMatch {
    applicationUuid: string;
}

interface IState {
    editModalState: ModalStateEnum;
    sendFormalApprovalUuid: string;
    sendModalState: ModalStateEnum;
}

interface IProps {
    match: routerMatch<IMatch>;
}

interface IPropsSelector {
    application: IApplication;
    currentUser: IAuthUser;
    formals: IDictionary<IFormal>;
    isWorkflowStatusMoveInProgress: boolean;
}

interface IPropsDispatch {
    applicationGet: () => void;
    formalAdd: () => void;
    formalsList: () => void;
    sendToBorrower: (formalApprovalUuid: string) => void;
    sendToBroker: (formalApprovalUuid: string) => void;
}

type Props = IProps & IPropsSelector & IPropsDispatch;

class FormalApprovals extends React.Component<Props> {
    public state: IState = {
        editModalState: ModalStateEnum.Off,
        sendFormalApprovalUuid: null,
        sendModalState: ModalStateEnum.Off,
    };

    constructor(props: Props) {
        super(props);

        this.onClickEdit = this.onClickEdit.bind(this);
        this.onClickIssueFormalApproval = this.onClickIssueFormalApproval.bind(this);
        this.onCloseFormalApprovalEditModal = this.onCloseFormalApprovalEditModal.bind(this);
        this.onCloseFormalApprovalSendModal = this.onCloseFormalApprovalSendModal.bind(this);
    }

    public componentDidMount() {
        const { application } = this.props;

        if (!application) {
            this.props.applicationGet();
        }

        this.props.formalsList();
    }

    public render(): JSX.Element {
        const { application, currentUser, formals, isWorkflowStatusMoveInProgress, match } = this.props;
        const { editModalState, sendModalState, sendFormalApprovalUuid } = this.state;

        if (!application || !formals) {
            return (
                <Layout applicationUuid={match.params.applicationUuid} section='formal-approvals'>
                    <Typography.Title level={2}>Formal Approvals</Typography.Title>
                    <Spin />
                </Layout>
            );
        }

        const currencyFormatter: Intl.NumberFormat = new Intl.NumberFormat('en-AU', {
            currency: 'AUD',
            style: 'currency',
        });

        const percentageFormatter: Intl.NumberFormat = Intl.NumberFormat('en-AU', {
            maximumFractionDigits: 2,
            minimumFractionDigits: 0,
            style: 'percent',
        });

        const columns: ColumnType<IFormal>[] = [
            {
                dataIndex: 'createdTime',
                defaultSortOrder: 'descend',
                render: (createdTime: string) => dayjs(createdTime).format('DD/MM/YYYY HH:mm'),
                sorter: (a: IFormal, b: IFormal) => dayjs(a.createdTime) > dayjs(b.createdTime) ? 1 : -1,
                title: 'Created',
                width: '15%',
            },
            {
                dataIndex: 'code',
                title: 'Code',
            },
            {
                dataIndex: 'loanAmount',
                render: (loanAmount: number) => currencyFormatter.format(loanAmount),
                title: 'Loan Amount',
                width: '15%',
            },
            {
                dataIndex: 'termMonths',
                render: (termMonths: number) => `${termMonths} months`,
                title: 'Term',
                width: '15%',
            },
            {
                dataIndex: 'interestRate',
                render: (interestRate: number) => percentageFormatter.format(interestRate / 100),
                title: 'Interest Rate',
                width: '15%',
            },
            {
                render: (formalApproval: IFormal) => {
                    if (!formalApproval.dealFileUuid) {
                        return null;
                    }

                    const isBroker: boolean = application.deal.isBroker || !!application.deal.brokerUuid;

                    const menu: MenuProps = {
                        items: [
                            {
                                key: 'send-to',
                                label: 'Send To',
                                onClick: () => this.onClickSendTo(formalApproval.uuid),
                            },
                        ],
                    };

                    const sendButtonBlock: JSX.Element = isBroker ? (
                        <Dropdown.Button
                            onClick={(): void => {
                                this.onClickSendToBroker(formalApproval.uuid);
                            }}
                            menu={menu}
                        >
                            Send to Broker
                        </Dropdown.Button>
                    ) : (
                        <Dropdown.Button
                            onClick={(): void => {
                                this.onClickSendToBorrower(formalApproval.uuid);
                            }}
                            menu={menu}
                        >
                            Send to Borrower
                        </Dropdown.Button>
                    );

                    const formalApprovalSendModalBlock: JSX.Element = formalApproval.uuid === sendFormalApprovalUuid && (
                        <FormalApprovalSendModal
                            formalApprovalUuid={sendFormalApprovalUuid}
                            sendModalState={sendModalState}
                            onCancel={this.onCloseFormalApprovalSendModal}
                        />
                    );

                    return (
                        <Space>
                            <Button
                                href={`${process.env.API_HOST}/application-formals/${formalApproval.uuid}/download`}
                                target='_blank'
                                rel='noreferrer'
                            >
                                View PDF
                            </Button>
                            {sendButtonBlock}
                            {formalApprovalSendModalBlock}
                        </Space>
                    );
                },
                width: '10%',
            },
        ];

        const issueApprovalButton: JSX.Element = currentUser.permissions.includes(PermissionsEnum.ApplicationFormalApprovalAdd) && (
            <Button className='issue-formal-approval' loading={isWorkflowStatusMoveInProgress} onClick={this.onClickIssueFormalApproval}>Issue Formal Approval</Button>
        );

        const formalApprovalEditModalBlock: JSX.Element = ModalStateEnum.Off !== editModalState && (
            <FormalApprovalEditModal
                applicationUuid={application.uuid}
                isOpen={editModalState}
                onCancel={this.onCloseFormalApprovalEditModal}
            />
        );

        return (
            <Layout applicationUuid={match.params.applicationUuid} section='formal-approvals'>
                <Space className='actions'>
                    <Button onClick={this.onClickEdit}>Edit</Button>
                    <Button href={`${process.env.API_HOST}/applications/${application.uuid}/formal-approval-pdf-preview`} target='_blank'>
                        Preview PDF
                    </Button>
                    {issueApprovalButton}
                </Space>
                <Typography.Title level={2}>Formal Approvals</Typography.Title>
                <Table
                    columns={columns}
                    dataSource={_.values(formals)}
                    pagination={false}
                    rowKey='uuid'
                    size='middle'
                />
                {formalApprovalEditModalBlock}
            </Layout>
        );
    }

    private onClickEdit(): void {
        this.setState({
            editModalState: ModalStateEnum.Open,
        });
    }

    private onCloseFormalApprovalEditModal(): void {
        this.setState({
            editModalState: ModalStateEnum.Closed,
        });
    }

    private onClickIssueFormalApproval() {
        Modal.confirm({
            content: 'Are you sure you want to issue a formal approval for this application?',
            okText: 'Issue Formal Approval',
            onOk: () => {
                this.props.formalAdd();
            },
            title: 'Issue Formal Approval',
        });
    }

    private onClickSendToBroker(formalApprovalUuid: string): void {
        const { formals } = this.props;
        const formalApproval: IFormal = formals[formalApprovalUuid];

        Modal.confirm({
            content: `Are you sure you want to send the Formal Approval ${formalApproval.code} to the broker?`,
            okText: 'Send',
            onOk: () => {
                this.props.sendToBroker(formalApprovalUuid);
            },
            title: 'Send to Broker',
        });
    }

    private onClickSendToBorrower(formalApprovalUuid: string): void {
        const { formals } = this.props;
        const formalApproval: IFormal = formals[formalApprovalUuid];

        Modal.confirm({
            content: `Are you sure you want to send the Formal Approval ${formalApproval.code} to the borrower?`,
            okText: 'Send',
            onOk: () => {
                this.props.sendToBorrower(formalApprovalUuid);
            },
            title: 'Send to Borrower',
        });
    }

    private onClickSendTo(sendFormalApprovalUuid: string): void {
        this.setState({
            sendFormalApprovalUuid,
            sendModalState: ModalStateEnum.Open,
        });
    }

    private onCloseFormalApprovalSendModal(): void {
        this.setState({
            sendModalState: ModalStateEnum.Closed,
        });
    }
}

function mapStateToProps(state: IGlobalState, ownProps: IProps): IPropsSelector {
    return {
        application: applicationSelector(state, ownProps.match.params.applicationUuid),
        currentUser: authCurrentUserSelector(state),
        formals: applicationFormalsSelector(state, ownProps.match.params.applicationUuid),
        isWorkflowStatusMoveInProgress: applicationWorkflowStatusMoveInProgressSelector(state),
    };
}

function mapDispatchToProps(dispatch: Dispatch, ownProps: IProps): IPropsDispatch {
    return {
        applicationGet: () => dispatch(applicationGetAction(ownProps.match.params.applicationUuid)),
        formalAdd: () => dispatch(applicationFormalsAddAction(ownProps.match.params.applicationUuid)),
        formalsList: () => dispatch(applicationFormalsListAction(ownProps.match.params.applicationUuid)),
        sendToBorrower: (formalApprovalUuid: string) => dispatch(applicationFormalApprovalSendToBorrowerAction(formalApprovalUuid)),
        sendToBroker: (formalApprovalUuid: string) => dispatch(applicationFormalApprovalSendToBrokerAction(formalApprovalUuid)),
    };
}

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(FormalApprovals);
